import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Save, Trash2, Edit3, Star, Eye, EyeOff, Sparkles, Receipt } from "lucide-react";
import ServiceVariantsEditor, { unitLabel } from "@/components/admin/ServiceVariantsEditor";
import ServiceSubscriptionsModal from "@/components/admin/ServiceSubscriptionsModal";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";
import NotifyContactsModal from "@/components/admin/NotifyContactsModal";
import ImageUpload from "@/components/ImageUpload";

const EMPTY = {
  title: "",
  description: "",
  price: 0,
  currency: "EUR",
  price_unit: "per_year",
  category: "",
  contact_email: "",
  cta_url: "",
  image: "",
  featured: false,
  active: true,
  order: 0,
  variants: [],
};

const seedVariants = (s) => (s.variants?.length ? s.variants
  : (s.price > 0 || s.price_unit === "on_quote") ? [{ id: `new-legacy`, name: "Formule standard", description: "", price: s.price || 0, price_unit: s.price_unit === "per_use" ? "one_time" : (s.price_unit || "per_year"), active: true }] : []);

export default function AdminSharedServices() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(new Set());
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [subsFor, setSubsFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/shared-services"); setItems(r.data || []); }
    catch { toast.error("Erreur de chargement"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setCreating(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s, variants: seedVariants(s) }); setCreating(true); };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const r = editing ? await api.patch(`/admin/shared-services/${editing.id}`, form) : await api.post("/admin/shared-services", form);
      const errs = (r.data.variants || []).filter((v) => v.stripe_error);
      if (errs.length) toast.warning(`Service enregistré, mais ${errs.length} variante(s) non synchronisée(s) avec Stripe : ${errs[0].stripe_error}`);
      else toast.success(editing ? "Service mis à jour" : "Service créé");
      setCreating(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Supprimer le service "${s.title}" ?`)) return;
    try { await api.delete(`/admin/shared-services/${s.id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const toggleActive = async (s) => {
    try { await api.patch(`/admin/shared-services/${s.id}`, { active: !s.active }); load(); }
    catch { toast.error("Erreur"); }
  };

  const toggleFeatured = async (s) => {
    try { await api.patch(`/admin/shared-services/${s.id}`, { featured: !s.featured }); load(); }
    catch { toast.error("Erreur"); }
  };

  // Bulk
  const toggleSel = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const clearSel = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(items.map((x) => x.id)));
  const bulkDelete = async () => {
    if (!window.confirm(`Supprimer ${selected.size} service${selected.size > 1 ? "s" : ""} ?`)) return;
    try {
      const r = await api.post("/admin/shared-services/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} supprimé${r.data.deleted > 1 ? "s" : ""}`);
      clearSel(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Services mutualisés</h1>
          <p className="mt-1 text-neutral-600">Catalogue de services réservés aux membres : achats groupés, conseil, assurance, comptabilité…</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-wine-600" data-testid="admin-shared-services-create-btn">
          <Plus className="h-4 w-4" /> Nouveau service
        </button>
      </div>

      {selected.size > 0 && (
        <div className="mt-4">
          <BulkActionsBar count={selected.size} total={items.length} onClear={clearSel} onSelectAll={selectAll} onDelete={bulkDelete} label="service" testId="admin-shared-services-bulk" extraActions={
            <button onClick={() => setNotifyOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="shared-services-bulk-notify">
              🔔 Notifier des contacts
            </button>
          } />
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-neutral-50 text-xs text-neutral-600">
            <tr>
              <th className="px-3 py-2 w-8"><BulkCheckbox checked={items.length > 0 && selected.size === items.length} onChange={(v) => v ? selectAll() : clearSel()} testId="services-select-all" /></th>
              <th className="px-4 py-2 text-left">Service</th>
              <th className="px-4 py-2 text-left">Catégorie</th>
              <th className="px-4 py-2 text-right">Prix</th>
              <th className="px-4 py-2 text-center">Statut</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className={`border-t border-neutral-100 ${selected.has(s.id) ? "bg-rose-50/40" : ""}`} data-testid={`service-row-${s.id}`}>
                <td className="px-3 py-3"><BulkCheckbox checked={selected.has(s.id)} onChange={() => toggleSel(s.id)} testId={`service-select-${s.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-semibold text-navy">
                    {s.featured && <Star className="h-4 w-4 fill-amber-400 text-amber-500" />}
                    {s.title}
                  </div>
                  {s.description && <div className="mt-0.5 line-clamp-1 text-xs text-neutral-500">{s.description}</div>}
                </td>
                <td className="px-4 py-3 text-neutral-700">{s.category || "—"}</td>
                <td className="px-4 py-3 text-right font-semibold" data-testid={`service-price-${s.id}`}>
                  {s.variants?.length > 1 && <div className="text-[10px] font-normal text-neutral-500">{s.variants.length} variantes · dès</div>}
                  {s.price_unit === "on_quote" ? <span className="text-neutral-500 italic">Sur devis</span>
                    : <>{Number(s.price || 0).toFixed(0)} {s.currency || "EUR"} <span className="text-xs font-normal text-neutral-500">{unitLabel(s.price_unit)}</span></>}
                  {s.variants?.some((v) => v.stripe_error) && <div className="text-[10px] font-semibold text-rose-600">⚠ Stripe à resynchroniser</div>}
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleActive(s)} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`} data-testid={`service-toggle-${s.id}`}>
                    {s.active ? <><Eye className="h-3 w-3" />Visible</> : <><EyeOff className="h-3 w-3" />Masqué</>}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => toggleFeatured(s)} className={`grid h-7 w-7 place-items-center rounded-full hover:bg-amber-50 ${s.featured ? "text-amber-500" : "text-neutral-400 hover:text-amber-500"}`} title={s.featured ? "Retirer de la une" : "Mettre à la une"} data-testid={`service-featured-${s.id}`}><Star className={`h-3.5 w-3.5 ${s.featured ? "fill-current" : ""}`} /></button>
                    <button onClick={() => setSubsFor(s)} className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-[11px] font-semibold text-wine-700 hover:bg-wine-50" title="Souscriptions & facturation" data-testid={`service-subs-${s.id}`}><Receipt className="h-3.5 w-3.5" />Souscriptions</button>
                    <button onClick={() => openEdit(s)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier" data-testid={`service-edit-${s.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(s)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`service-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-neutral-500">Aucun service. Cliquez « Nouveau service » pour démarrer le catalogue.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setCreating(false)} data-testid="service-modal">
          <form onSubmit={save} className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <h2 className="flex items-center gap-2 text-xl font-bold text-navy"><Sparkles className="h-5 w-5 text-wine-500" />{editing ? "Modifier le service" : "Nouveau service mutualisé"}</h2>
              <button type="button" onClick={() => setCreating(false)} className="rounded-full p-1 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-5 grid gap-3">
              <input required placeholder="Titre du service (ex: Achat groupé bouteilles légères)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="service-form-title" />
              <textarea rows={4} placeholder="Description (les avantages, les conditions, les contacts…)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="service-form-description" />

              <div className="flex items-center gap-2 text-sm text-neutral-600">
                Devise
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="service-form-currency">
                  <option>EUR</option><option>USD</option><option>GBP</option><option>CHF</option>
                </select>
              </div>
              <ServiceVariantsEditor value={form.variants || []} onChange={(variants) => setForm((f) => ({ ...f, variants }))} currency={form.currency} />

              <div className="grid gap-3 sm:grid-cols-2">
                <input placeholder="Catégorie (ex: Achat groupé, Conseil, Assurance)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="service-form-category" />
                <input type="number" placeholder="Ordre d'affichage" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="service-form-order" />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <input type="email" placeholder="Email contact (devis, info)" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="service-form-email" />
                <input type="url" placeholder="Lien externe (CTA optionnel, https://…)" value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2.5 text-sm" data-testid="service-form-cta" />
              </div>

              <div className="rounded-xl border-2 border-dashed border-neutral-200 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Visuel (optionnel)</div>
                <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="service-form-image" />
              </div>

              <div className="flex flex-wrap gap-5">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4" data-testid="service-form-featured" />
                  <Star className="h-3.5 w-3.5 text-amber-500" /> Mettre à la une
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4" data-testid="service-form-active" />
                  Service visible pour les membres
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="rounded-full border border-neutral-300 bg-white px-5 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60" data-testid="service-form-save">
                <Save className="h-4 w-4" /> {saving ? "Synchronisation Stripe…" : editing ? "Mettre à jour" : "Créer le service"}
              </button>
            </div>
          </form>
        </div>
      )}

      {subsFor && <ServiceSubscriptionsModal service={subsFor} onClose={() => setSubsFor(null)} />}

      {notifyOpen && (
        <NotifyContactsModal
          open={notifyOpen}
          onClose={() => { setNotifyOpen(false); clearSel(); }}
          entityType="shared_service"
          entityIds={Array.from(selected)}
          endpoint="/admin/shared-services/notify-contacts-bulk"
          payloadKey="service_ids"
        />
      )}
    </PortalLayout>
  );
}
